import React from "react";

type UserType = {
    id: number,
    login: string,
    name: string,
    avatar: string
}

// Определяем тип контекста
type AuthContextType = {
    isAuthenticated: boolean; // флаг, показывающий, аутентифицирован ли пользователь
    setAuth: (auth: boolean) => void; // функция для изменения значения isAuthenticated
    roles: string[],
    setRoles: (roles: string[]) => void,
    user: UserType,
    setUser: (user: UserType) => void,

    // ✅ helpers
    hasRole: (role: string) => boolean;
    hasAnyRole: (...roles: string[]) => boolean;
    hasAllRoles: (...roles: string[]) => boolean;

    // ✅ можно добавить часто используемые “алиасы”
    isAdmin: boolean;
    isManager: boolean;
    isPartner: boolean;
    isCurator: boolean;
    isAdminOrManager: boolean;
    isAdminOrManagerOrPartner: boolean;

};

// Создаем контекст с типом AuthContextType и начальными значениями по умолчанию
const AuthContext = React.createContext<AuthContextType>({
    isAuthenticated: false,
    setAuth: () => {},
    roles: ['anonymous'],
    setRoles: () => {},
    user: {id: 0, login: 'anonymous', name: 'anonymous', avatar: ''},
    setUser: () => {},
    hasRole: () => false,
    hasAnyRole: () => false,
    hasAllRoles: () => false,
    isAdmin: false,
    isManager: false,
    isPartner: false,
    isCurator: false,
    isAdminOrManager: false,
    isAdminOrManagerOrPartner: false,
});

// Создаем компонент провайдера, который предоставляет данные контекста всем дочерним компонентам
export const AuthProvider = ({ children }: { children: React.JSX.Element }) => {
    // Используем хук useState для создания переменной isAuthenticated и функции setAuth для ее изменения
    const [isAuthenticated, setAuth] = React.useState<boolean>(false);
    const [roles, setRoles] = React.useState<string[]>(['anonymous']);
    const [user, setUser] = React.useState<UserType>({id: 0, login: 'anonymous', name: 'anonymous', avatar: ''});

    React.useEffect(() => {
        if (localStorage.getItem('token')) {
            setAuth(true);
        }
        if (localStorage.getItem('roles')) {
            const s = localStorage.getItem('roles');
            if (s) {
                setRoles(JSON.parse(s));
            }
        }
        if (localStorage.getItem('user')) {
            const s = localStorage.getItem('user');
            if (s) {
                setUser(JSON.parse(s));
            }
        }
    }, [])

    // ✅ быстрые проверки через Set (и без пересоздания на каждом рендере)
    const rolesSet = React.useMemo(() => new Set<string>(roles), [roles]);

    const hasRole = React.useCallback((role: string) => rolesSet.has(role), [rolesSet]);

    const hasAnyRole = React.useCallback(
        (...required: string[]) => required.some((r) => rolesSet.has(r)),
        [rolesSet]
    );

    const hasAllRoles = React.useCallback(
        (...required: string[]) => required.every((r) => rolesSet.has(r)),
        [rolesSet]
    );

    // ✅ алиасы
    const isAdmin = hasRole("ROLE_ADMIN");
    const isManager = hasRole("ROLE_MANAGER");
    const isPartner = hasRole("ROLE_PARTNER");
    const isCurator = hasRole("ROLE_CURATOR");
    const isAdminOrManager = hasAnyRole("ROLE_ADMIN", "ROLE_MANAGER");
    const isAdminOrManagerOrPartner = hasAnyRole("ROLE_ADMIN", "ROLE_MANAGER", "ROLE_PARTNER");


    // Возвращаем контекст провайдера, передавая значения isAuthenticated и setAuth в качестве значения контекста
    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            setAuth,
            roles,
            setRoles,
            user,
            setUser,
            hasRole,
            hasAnyRole,
            hasAllRoles,
            isAdmin,
            isManager,
            isPartner,
            isCurator,
            isAdminOrManager,
            isAdminOrManagerOrPartner,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;

